import Image from 'next/image';
import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable } from '@/design-system/components/ui';

import { BlendPositionSide, formatUtilizationCondition, Trigger } from '../../domain';
import { AddressChip } from './components/AddressChip';

interface Props {
  trigger: Trigger;
}

export const BlendTriggerFullDetails = ({ trigger }: Props) => {
  const poolAddress = trigger.context?.pool_address ?? '';
  const assetAddress = trigger.context?.asset ?? '';

  const sideLabel = useMemo(() => {
    const side = trigger.context?.type;
    return side === BlendPositionSide.Supply ? 'Supply' : side === BlendPositionSide.Borrow ? 'Borrow' : '-';
  }, [trigger.context]);

  const contractDisplay = (address: string) => (
    <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
      <AddressChip
        address={address}
        scannerUrl={`https://stellar.expert/explorer/public/contract/${address}`}
        hideChainIcon
      />
    </div>
  );

  const networkDisplay = (
    <div className="tw:flex tw:flex-row tw:items-center tw:gap-2">
      <Image src="/assets/webp/stellar.webp" alt="Stellar" width={20} height={20} />
      <span>Stellar</span>
    </div>
  );

  const sideDisplay = useMemo(() => {
    if (sideLabel === '-') return '-';

    return <Badge variant={sideLabel === 'Supply' ? 'success' : 'warning'}>{sideLabel}</Badge>;
  }, [sideLabel]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Pool" value={poolAddress ? '' : '-'}>
        {poolAddress ? (
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center tw:flex-wrap tw:ml-2">
            {contractDisplay(poolAddress)}
          </div>
        ) : null}
      </KeyValueRow>
      <KeyValueRow label="Asset" value={assetAddress ? '' : '-'}>
        {assetAddress ? (
          <div className="tw:flex tw:flex-row tw:gap-1 tw:items-center tw:flex-wrap tw:ml-2">
            {contractDisplay(assetAddress)}
          </div>
        ) : null}
      </KeyValueRow>
      <KeyValueRow label="Network" value={networkDisplay} />
      <KeyValueRow label="Side" value={sideDisplay} />
      {/* The volume currency is the reserve asset itself and travels with each event, so there is
          no trigger-level currency to show the way the token-holders trigger does. */}
      <KeyValueRow label="Argument conditions" value={formatUtilizationCondition(trigger.conditionExpression)} />
    </KeyValueTable>
  );
};
